import type { MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { partnersImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  usePanelContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { PartnersItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/partnersItemAttributes';

import PartnerUI from '../Partner/components/PartnerUI';
import { PARTNERS_LOGOS } from '../Partner/constants';

const PartnersShifted: BuilderComponent<PartnersItemAttributes> = () => <div />; // never used

interface PartnersShiftedItem
  extends Omit<DocumentItem<PartnersItemAttributes>, 'children'> {
  children: PartnersShiftedItem[];
  index?: number;
  partnerImageAlt?: '';
  partnerImageUrl: string;
  partnersCount: number;
}

interface PartnersShiftedUiProps {
  item?: PartnersShiftedItem;
  onClickCallBack?: (logo: PartnersShiftedItem) => void;
  onDoubleClickCallBack?: (e: MouseEvent, logo: PartnersShiftedItem) => void;
  subdomainNetworkUrl?: string;
  withImageSetter?: boolean;
}

const PartnersShiftedUi = ({
  item,
  onClickCallBack,
  onDoubleClickCallBack,
  subdomainNetworkUrl,
  withImageSetter,
}: PartnersShiftedUiProps) => {
  const items = item?.children;

  if (!items) {
    return <div data-testid="partners-no-partners" />;
  }

  return (
    <Wrapper>
      {items.map((partner, index) => {
        if (!partner) {
          return null;
        }

        const {
          _imageData,
          logoBackground,
          partnerImage,
          isLoadingSrc,
          openInNewTab,
        } = partner.attributes || {};
        const { partnerImageAlt, partnerImageUrl } = partner;

        return (
          <Box key={partner.id}>
            <LogoWrapper
              bg={logoBackground}
              dataTestId={`partners-shifted-logo-wrapper-${index}`}
              $isFirstLogo={index === 0}
              $isThirdLogo={items.length > 6 && index === 3}
              onClick={() => onClickCallBack?.(partner)}
              onDoubleClick={(e) => onDoubleClickCallBack?.(e, partner)}
            >
              <PartnerUI
                isLoadingSrc={isLoadingSrc}
                index={(partner?.index || 0) % PARTNERS_LOGOS.length}
                openInNewTab={openInNewTab}
                padding={{ _: 2, m: 3 }}
                partnerImage={_imageData || partnerImage}
                partnerImageAlt={partnerImageAlt}
                // we do not want to send partnerImageUrl if we are in the Builder
                partnerImageUrl={
                  !withImageSetter && partnerImageUrl ? partnerImageUrl : ''
                }
                subdomainNetworkUrl={subdomainNetworkUrl}
              />
            </LogoWrapper>
            {withImageSetter && (
              <ImageSetter
                attributeName="partnerImage"
                item={partner}
                imageSize={partnersImageRecommendedSize}
              />
            )}
          </Box>
        );
      })}
    </Wrapper>
  );
};

const RenderBuilder = () => {
  const { getParentItem, updateAttribute } = useBuilderContext();
  const { item } = useComponentContext();
  const {
    hivebrite: { subdomainNetworkUrl },
  } = useBuilderConfigContext();
  const { displayPanel } = usePanelContext();
  const column = getParentItem(item.id);
  const parentComponent = getParentItem(column.id);

  const toggleDrawer = (logo) => {
    const isOpened = parentComponent.attributes.__isSelected === logo.id;

    if (isOpened) {
      return;
    }

    updateAttribute({
      id: parentComponent.id,
      attribute: '__isSelected',
      value: logo.id,
    });
  };

  return (
    <PartnersShiftedUi
      item={item as PartnersShiftedItem}
      onClickCallBack={toggleDrawer}
      onDoubleClickCallBack={(e, partner) => {
        e.stopPropagation();
        displayPanel();
        toggleDrawer(partner);
      }}
      subdomainNetworkUrl={subdomainNetworkUrl}
      withImageSetter
    />
  );
};

const Render = ({ item }: RenderProps<PartnersShiftedItem>) => {
  const { subdomainNetworkUrl } = useRendererContext();

  return (
    <PartnersShiftedUi
      item={item}
      subdomainNetworkUrl={subdomainNetworkUrl}
      withImageSetter={false}
    />
  );
};

PartnersShifted.componentName = 'PartnersShifted';
PartnersShifted.componentType = 'single';

PartnersShifted.canBeDoubleClicked = false;
PartnersShifted.canBeDragged = false;
PartnersShifted.canBeListed = false;
PartnersShifted.canBeSelected = false;

PartnersShifted.RenderBuilder = RenderBuilder;
PartnersShifted.Render = Render;

PartnersShifted.item = ({ attributes, children }) => ({
  attributes,
  children,
  type: PartnersShifted.componentName,
});

export default PartnersShifted;

interface LogoWrapperProps {
  $isFirstLogo: boolean;
  $isThirdLogo: boolean;
}

const LogoWrapper = styled(Box).attrs(
  ({ $isFirstLogo, $isThirdLogo }: LogoWrapperProps) => ({
    width: { xl: 210, l: 222, m: 226, s: 148 },
    height: { _: 62, m: 82 },
    mr: { xl: 6 },
    mb: 6,
    ml: { xl: $isFirstLogo ? 190 : $isThirdLogo && 65 },
    mx: { s: 1, m: 1, l: 3 },
  }),
)<LogoWrapperProps>`
  ${({ theme }) =>
    css`
      border-radius: ${theme.themeVariables.globalBorderRadius}px;
    `}
`;

const Wrapper = styled(Box).attrs(() => ({
  justifyContent: 'center',
  flexWrap: 'wrap',
  mt: { s: 8, xl: 0 },
  ml: { xl: -200 },
}))``;
